import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import type { MessageText, MessageTextGroup, MessageTexts } from '../types'

/**
 * « Textes des variables » : les phrases que le système glisse dans les
 * messages.
 *
 * Un modèle cite `{{order_details}}` ou `{{practical_info}}` ; le contenu de
 * ces variables est composé par le serveur avec des phrases — « Ouverture des
 * portes : {time}. », « retirez vos plats au comptoir… ». Elles étaient
 * écrites dans le code : personne ne pouvait les corriger. Chacune se réécrit
 * ici, par langue, pour cette édition. Le texte de la plateforme reste
 * intact : « Revenir au texte de la plateforme » supprime la version de
 * l'édition.
 *
 * Syntaxe : `{nom}`, une seule accolade — la double `{{…}}` est celle des
 * modèles. Le serveur refuse une valeur inconnue, une valeur obligatoire
 * oubliée ou une accolade isolée ; l'écran le dit avant même l'envoi.
 */

const TOKEN = /\{([a-z_]+)\}/g
const ANY_BRACED = /\{([^{}]*)\}/g

/** Même remplissage que le serveur (`phrases.fill`) : seuls les `{nom}` connus. */
function fill(text: string, values: Record<string, string>): string {
  return text.replace(TOKEN, (whole, name: string) => values[name] ?? whole)
}

type Problem =
  | { kind: 'empty' }
  | { kind: 'stray' }
  | { kind: 'unknown'; names: string[] }
  | { kind: 'missing'; names: string[] }

/** Les mêmes règles que `phrases.validate` côté serveur, dans le même ordre. */
export function phraseProblems(text: string, entry: MessageText): Problem[] {
  const cleaned = text.trim()
  if (!cleaned) return entry.allow_empty ? [] : [{ kind: 'empty' }]
  const allowed = new Set(entry.placeholders.map((item) => item.name))
  const cited = new Set<string>()
  const unknown: string[] = []
  for (const match of cleaned.matchAll(ANY_BRACED)) {
    const name = match[1] ?? ''
    if (allowed.has(name)) cited.add(name)
    else if (!unknown.includes(name)) unknown.push(name)
  }
  const rest = cleaned.replace(ANY_BRACED, '')
  if (rest.includes('{') || rest.includes('}')) return [{ kind: 'stray' }]
  if (unknown.length > 0) return [{ kind: 'unknown', names: unknown }]
  const missing = entry.placeholders
    .filter((item) => item.required && !cited.has(item.name))
    .map((item) => item.name)
  return missing.length > 0 ? [{ kind: 'missing', names: missing }] : []
}

function pick(value: Record<string, string>, language: string): string {
  const base = language.slice(0, 2)
  return value[base] ?? value.fr ?? Object.values(value)[0] ?? ''
}

export function MessageTextsEditor({
  eventId,
  locale,
  focusGroup,
}: {
  eventId: string | undefined
  /** La langue des messages qu'on réécrit (FR / DE / EN, choisie en tête de page). */
  locale: string
  /** Groupe à ouvrir d'office — quand on arrive depuis une variable d'un modèle. */
  focusGroup: string | null
}) {
  const { t, i18n } = useTranslation()
  const query = useAdminQuery<MessageTexts>(
    ['admin-message-texts', eventId],
    `/admin/events/${eventId}/message-texts`,
    eventId,
  )
  const [open, setOpen] = useState<string | null>(focusGroup)
  const focused = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (focusGroup) {
      setOpen(focusGroup)
      // Arrivé depuis une variable : le groupe est ouvert ET visible.
      window.requestAnimationFrame(() =>
        focused.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      )
    }
  }, [focusGroup])

  if (query.isPending) return <Skeleton className="h-64 w-full" />
  if (query.isError) {
    return <p className="text-sm text-state-danger">{query.error.message}</p>
  }

  const samples = query.data.samples[locale] ?? {}

  return (
    <div className="grid grid-cols-1 gap-2" data-testid="message-texts">
      <p className="mb-2 text-sm text-content-secondary">{t('admin.messageTexts.intro')}</p>
      {query.data.groups.map((group) => {
        const changed = group.texts.filter((entry) => entry.overrides[locale] !== undefined).length
        const isOpen = open === group.code
        return (
          <div key={group.code} ref={group.code === focusGroup ? focused : undefined}>
            <Card className="p-0">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : group.code)}
                aria-expanded={isOpen}
                className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-content-primary">
                    {pick(group.label, i18n.language)}
                  </span>
                  <span className="mt-0.5 block text-xs text-content-muted">
                    {t('admin.messageTexts.count', { count: group.texts.length })}
                    {changed > 0
                      ? ` · ${t('admin.messageTexts.changedCount', { count: changed })}`
                      : null}
                  </span>
                </span>
                <span aria-hidden className="shrink-0 text-content-muted">
                  {isOpen ? '−' : '+'}
                </span>
              </button>
              {isOpen ? (
                <GroupBody group={group} eventId={eventId} locale={locale} samples={samples} />
              ) : null}
            </Card>
          </div>
        )
      })}
    </div>
  )
}

function GroupBody({
  group,
  eventId,
  locale,
  samples,
}: {
  group: MessageTextGroup
  eventId: string | undefined
  locale: string
  samples: Record<string, string>
}) {
  const { t, i18n } = useTranslation()
  const [editing, setEditing] = useState<string | null>(null)
  return (
    <div className="border-t border-line-subtle px-4 py-4">
      <p className="text-sm text-content-secondary">{pick(group.description, i18n.language)}</p>
      {group.variables.length > 0 ? (
        <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-content-muted">
          <span>{t('admin.messageTexts.feeds')}</span>
          {group.variables.map((name) => (
            <code
              key={name}
              className="break-all rounded bg-bg-base px-1.5 py-0.5 font-mono text-champagne-300"
            >
              {`{{${name}}}`}
            </code>
          ))}
        </p>
      ) : (
        <p className="mt-2 text-xs text-content-muted">{t('admin.messageTexts.layoutOnly')}</p>
      )}
      <ul className="mt-4 space-y-2">
        {group.texts.map((entry) => (
          <li key={entry.key}>
            <PhraseRow
              entry={entry}
              eventId={eventId}
              locale={locale}
              samples={samples}
              editing={editing === entry.key}
              onEdit={() => setEditing(editing === entry.key ? null : entry.key)}
              onClose={() => setEditing(null)}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

function PhraseRow({
  entry,
  eventId,
  locale,
  samples,
  editing,
  onEdit,
  onClose,
}: {
  entry: MessageText
  eventId: string | undefined
  locale: string
  samples: Record<string, string>
  editing: boolean
  onEdit: () => void
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const override = entry.overrides[locale]
  const fallback = entry.defaults[locale] ?? entry.defaults.de ?? ''
  const current = override ?? fallback
  return (
    <div
      className="min-w-0 rounded-md border border-line-subtle bg-bg-base/40 px-3 py-2.5"
      data-testid={`phrase-${entry.key}`}
    >
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-content-muted">{pick(entry.when, i18n.language)}</p>
          <p
            className={cn(
              'mt-1 whitespace-pre-line break-words text-sm [overflow-wrap:anywhere]',
              current ? 'text-content-primary' : 'italic text-content-muted',
            )}
          >
            {current || t('admin.messageTexts.emptyText')}
          </p>
          <p className="mt-1 break-all font-mono text-[10px] text-content-muted">{entry.key}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {override !== undefined ? (
            <Badge tone="champagne">{t('admin.messageTexts.changed')}</Badge>
          ) : null}
          <Button size="sm" variant="outline" onClick={onEdit} aria-expanded={editing}>
            {editing ? t('admin.messageTexts.close') : t('admin.messageTexts.edit')}
          </Button>
        </div>
      </div>
      {editing ? (
        <PhraseEditor
          key={`${entry.key}:${locale}`}
          entry={entry}
          eventId={eventId}
          locale={locale}
          samples={samples}
          initial={current}
          onDone={onClose}
        />
      ) : null}
    </div>
  )
}

function PhraseEditor({
  entry,
  eventId,
  locale,
  samples,
  initial,
  onDone,
}: {
  entry: MessageText
  eventId: string | undefined
  locale: string
  samples: Record<string, string>
  initial: string
  onDone: () => void
}) {
  const { t, i18n } = useTranslation()
  const [text, setText] = useState(initial)
  const area = useRef<HTMLTextAreaElement>(null)
  const invalidate = [
    ['admin-message-texts', eventId ?? ''],
    // Les exemples des modèles sont composés de ces phrases.
    ['admin-templates', eventId ?? ''],
  ]
  const save = useAdminMutation<MessageText, { key: string; locale: string; text: string }>(
    'put',
    () => `/admin/events/${eventId}/message-texts`,
    eventId,
    invalidate,
  )
  const reset = useAdminMutation<MessageText, Record<string, never>>(
    'delete',
    () =>
      `/admin/events/${eventId}/message-texts/${encodeURIComponent(entry.key)}/${locale}`,
    eventId,
    invalidate,
  )

  const problems = useMemo(() => phraseProblems(text, entry), [text, entry])
  const platform = entry.defaults[locale] ?? entry.defaults.de ?? ''
  const hasOverride = entry.overrides[locale] !== undefined
  const unchanged = text.trim() === initial.trim()
  const preview = fill(text.trim(), samples)

  function insert(name: string) {
    const token = `{${name}}`
    const node = area.current
    const start = node?.selectionStart ?? text.length
    const end = node?.selectionEnd ?? text.length
    const next = text.slice(0, start) + token + text.slice(end)
    setText(next)
    window.requestAnimationFrame(() => {
      node?.focus()
      node?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const describe = (problem: Problem) => {
    const list = (names: string[]) => names.map((name) => `{${name}}`).join(', ')
    switch (problem.kind) {
      case 'empty':
        return t('admin.messageTexts.problem.empty')
      case 'stray':
        return t('admin.messageTexts.problem.stray')
      case 'unknown':
        return t('admin.messageTexts.problem.unknown', { names: list(problem.names) })
      case 'missing':
        return t('admin.messageTexts.problem.missing', { names: list(problem.names) })
    }
  }

  // Le refus du serveur, dit dans la langue de l'écran : ses codes portent
  // les noms en cause, le message brut est en français.
  const serverError = save.error ?? reset.error
  const serverMessage = (() => {
    if (!serverError) return null
    if (!(serverError instanceof ApiError)) return String(serverError)
    const names = (field: string) => {
      const value = serverError.details[0]?.[field]
      return Array.isArray(value) ? value.map(String) : []
    }
    switch (serverError.code) {
      case 'MESSAGE_TEXT_EMPTY':
        return describe({ kind: 'empty' })
      case 'MESSAGE_TEXT_STRAY_BRACE':
        return describe({ kind: 'stray' })
      case 'MESSAGE_TEXT_UNKNOWN_PLACEHOLDER':
        return describe({ kind: 'unknown', names: names('unknown') })
      case 'MESSAGE_TEXT_MISSING_PLACEHOLDER':
        return describe({ kind: 'missing', names: names('missing') })
      default:
        return serverError.message
    }
  })()
  const fieldId = `phrase-${entry.key}-${locale}`

  return (
    <div className="mt-3 border-t border-line-subtle pt-3">
      <label
        htmlFor={fieldId}
        className="block text-caption uppercase tracking-wide text-content-muted"
      >
        {t('admin.messageTexts.textLabel', { language: locale.toUpperCase() })}
      </label>
      <textarea
        ref={area}
        id={fieldId}
        value={text}
        rows={3}
        onChange={(event) => setText(event.target.value)}
        className="mt-1.5 w-full resize-y rounded-md border border-line bg-bg-base px-3 py-2.5 text-sm leading-relaxed text-content-primary"
      />

      {entry.placeholders.length > 0 ? (
        <div className="mt-2">
          <p className="text-xs text-content-muted">{t('admin.messageTexts.values')}</p>
          <ul className="mt-1.5 space-y-1.5">
            {entry.placeholders.map((item) => (
              <li key={item.name} className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => insert(item.name)}
                  aria-label={t('admin.messageTexts.insert', { name: item.name })}
                >
                  <code className="font-mono">{`{${item.name}}`}</code>
                </Button>
                <span className="min-w-0 flex-1 text-xs text-content-secondary">
                  {pick(item.description, i18n.language)}
                  {item.required ? ` · ${t('admin.messageTexts.required')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-2 text-xs text-content-muted">{t('admin.messageTexts.noValues')}</p>
      )}

      {problems.length > 0 ? (
        <div
          role="alert"
          className="mt-3 rounded-md border border-state-danger/40 bg-state-danger/5 px-3 py-2 text-sm text-state-danger"
        >
          {problems.map((problem) => (
            <p key={problem.kind}>{describe(problem)}</p>
          ))}
        </div>
      ) : null}
      {serverMessage ? (
        <p role="alert" className="mt-3 text-sm text-state-danger" data-testid="phrase-error">
          {serverMessage}
        </p>
      ) : null}

      <div className="mt-3 rounded-md border border-line-subtle bg-bg-base px-3 py-2.5">
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.messageTexts.preview')}
        </p>
        <p
          className="mt-1 whitespace-pre-line break-words text-sm text-content-primary [overflow-wrap:anywhere]"
          data-testid="phrase-preview"
        >
          {preview || t('admin.messageTexts.previewEmpty')}
        </p>
      </div>

      <p className="mt-3 text-xs text-content-muted">
        <span className="font-medium">{t('admin.messageTexts.platform')}</span>{' '}
        <span className="whitespace-pre-line break-words [overflow-wrap:anywhere]">
          {platform || t('admin.messageTexts.emptyText')}
        </span>
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={save.isPending || problems.length > 0 || unchanged}
          onClick={() => save.mutate({ key: entry.key, locale, text }, { onSuccess: onDone })}
        >
          {t('admin.messageTexts.save')}
        </Button>
        {hasOverride ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={reset.isPending}
            onClick={() => reset.mutate({}, { onSuccess: onDone })}
          >
            {t('admin.messageTexts.reset')}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('admin.messageTexts.cancel')}
        </Button>
      </div>
    </div>
  )
}
